import { useEffect } from 'react';
import { useAudienceStore, type ProductAnswers } from '@/stores/audience-store';
import type { CatalogField, CatalogOption, CatalogSection } from '@/app/audience-products/services';

/** Find a field by id within a section, throwing if missing — catalog drift guard. */
export const requireField = (
  fields: CatalogField[],
  id: string,
  context = 'catalog',
): CatalogField => {
  const found = fields.find((f) => f.id === id);
  if (!found) throw new Error(`${context}: missing field "${id}"`);
  return found;
};

/** Type-narrowing helper — accept the answer-shape union and return a string array. */
export const asArray = (v: unknown): string[] => (Array.isArray(v) ? (v as string[]) : []);

/**
 * The options a checklist offers: every live option, plus a retired one only while it is still
 * ticked (NPJB-796). Keeping that box mounted is what keeps the value, because antd's
 * `Checkbox.Group` drops any value it has no box for on the next change.
 */
export const offeredOptions = (
  options: CatalogOption[] | undefined,
  selected: string[],
): CatalogOption[] => (options ?? []).filter((o) => !o.retired || selected.includes(o.id));

/** Type-narrowing helper — return the string value or undefined. */
export const asString = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);

/** True when a value is empty (null, undefined, empty string, or empty array). */
export const isEmptyAnswer = (v: unknown): boolean =>
  v === null ||
  v === undefined ||
  (typeof v === 'string' && v.trim().length === 0) ||
  (Array.isArray(v) && v.length === 0);

/**
 * Evaluate a field's `showWhen` rule against the current answers. Fields without
 * a showWhen rule are always visible.
 */
export const evalShowWhen = (field: CatalogField, answers: ProductAnswers | undefined): boolean => {
  if (!field.showWhen) return true;
  const v = answers?.[field.showWhen.fieldId];
  if (Array.isArray(v)) return v.some((x) => field.showWhen?.anyOf.includes(x) ?? false);
  if (typeof v === 'string') return field.showWhen.anyOf.includes(v);
  return false;
};

/**
 * Generic walker — collect the labels of every required field in `sections`
 * whose `showWhen` rule is currently met but whose value is empty. This is the
 * default validation path, and the ONLY one for PGN and Editorial since their
 * grouped checklists were flattened; Display Media layers its ABM/CBM
 * per-lane minimum on top (see `get-missing-for-display.ts`).
 */
export const walkRequiredFields = (
  sections: CatalogSection[],
  answers: ProductAnswers | undefined,
): string[] => {
  const missing: string[] = [];
  for (const section of sections) {
    for (const field of section.fields) {
      if (!field.required) continue;
      if (!evalShowWhen(field, answers)) continue;
      if (isEmptyAnswer(answers?.[field.id])) missing.push(field.label);
    }
  }
  return missing;
};

/** Single iteration step of the cascade — extracted to keep the effect's
 *  cognitive complexity within the SonarQube limit. */
const clearIfHidden = (
  productId: string,
  field: CatalogField,
  answers: ProductAnswers,
  setAnswer: (productId: string, key: string, value: null) => void,
): void => {
  if (!field.showWhen) return;
  if (evalShowWhen(field, answers)) return;
  if (isEmptyAnswer(answers[field.id])) return;
  setAnswer(productId, field.id, null);
};

/**
 * Cascade-cleanup hook — for every field whose `showWhen` rule is no longer
 * met, null out its stored answer so deselecting a gating sub-product doesn't
 * leak stale data into payloads. Catalog-driven; one call replaces the
 * per-configurator useEffect duplication.
 */
export const useShowWhenCascade = (productId: string, sections: CatalogSection[]): void => {
  const answers = useAudienceStore((s) => s.products.productAnswers[productId]);
  const setAnswer = useAudienceStore((s) => s.setProductAnswer);

  useEffect(() => {
    if (!answers) return;
    for (const section of sections) {
      for (const field of section.fields) {
        clearIfHidden(productId, field, answers, setAnswer);
      }
    }
  }, [answers, sections, productId, setAnswer]);
};
